/**
 * The Pitch toolbar's one save-status line (SE-8389, PV23 · D5): Saving… ·
 * Saved just now → Saved 2m ago · Couldn't save · Retry · Updated by another
 * editor · Reload. Owns the 60 s clock; the View is pure.
 * @module features/scouting/components/molecules/PitchSaveStatusLine
 */
import React, { memo, useMemo } from "react";

import { usePitchSaveClock } from "@/features/scouting/hooks/usePitchSaveClock";
import type { WatchlistPreferenceStatus } from "@/features/scouting/hooks/watchlistPreferenceOrderState";
import { pitchSaveStatusText } from "@/features/scouting/utils/pitchSaveStatus";

import type { PitchSaveSurface } from "e2e/testId";

import PitchSaveStatusLineView, {
  type PitchSaveStatusAction,
} from "./PitchSaveStatusLineView";

/** Props for {@link PitchSaveStatusLineContainer}. */
export interface PitchSaveStatusLineProps {
  readonly status: WatchlistPreferenceStatus;
  /** Epoch milliseconds of the last successful save. */
  readonly savedAt?: number;
  /** Reducer-set copy that wins over the default line for its status. */
  readonly message?: string;
  /**
   * Who saved the newer version ("Updated by A. Whitmore · Reload"); the
   * line falls back to "another editor" while no read carries a name
   * (SE-8392 adds `lastEditedBy` to the pitch reads).
   */
  readonly editorName?: string;
  /** What the line reads before anything has been saved. */
  readonly idleText: string;
  /** Re-sends the failed save; the line offers Retry only while given. */
  readonly onRetry?: () => void;
  /** Drops the held copy after a conflict; offers Reload only while given. */
  readonly onReload?: () => void;
  /** Which host mounts the line; selects its test identity (Pitch default). */
  readonly surface?: PitchSaveSurface;
}

/** The inline action's label, split off the default line's " · " suffix. */
const ACTION_LABELS: Readonly<Record<PitchSaveStatusAction, string>> = {
  retry: "Retry",
  reload: "Reload",
};

/**
 * Splits the default "{text} · {Action}" line into its text and its action.
 * A reducer message that is not the default (a refused re-apply) keeps its
 * whole text and offers nothing.
 * @param text - The full status line
 * @param action - The action the status could offer
 * @returns The text to show and the action to offer, if any
 */
const splitAction = (
  text: string,
  action: PitchSaveStatusAction | undefined
): { readonly text: string; readonly action?: PitchSaveStatusAction } => {
  const suffix = action ? ` · ${ACTION_LABELS[action]}` : "";
  return action && text.endsWith(suffix)
    ? { text: text.slice(0, -suffix.length), action }
    : { text };
};

/**
 * Derives the line and its inline action from the save state.
 * @param props - Save status, clock input and the recovery handlers
 * @returns The rendered status line
 */
const PitchSaveStatusLineContainer = (props: PitchSaveStatusLineProps) => {
  const now = usePitchSaveClock(props.savedAt);
  const line = useMemo(() => {
    const text = pitchSaveStatusText(props, now, props.idleText);
    if (props.status === "error" && props.onRetry) {
      return splitAction(text, "retry");
    }
    if (props.status === "conflict" && props.onReload) {
      return splitAction(text, "reload");
    }
    return { text };
  }, [now, props]);
  return (
    <PitchSaveStatusLineView
      text={line.text}
      status={props.status}
      action={line.action}
      onAction={line.action === "retry" ? props.onRetry : props.onReload}
      surface={props.surface}
    />
  );
};

PitchSaveStatusLineContainer.displayName = "PitchSaveStatusLineContainer";
export default memo(PitchSaveStatusLineContainer);
